import { Doughnut } from "react-chartjs-2";
import { CircularProgress, Box } from "@mui/material";
import { Chart, Plugin, ChartData, ChartOptions, LegendItem } from "chart.js";

interface DoughnutChartProps {
  data: ChartData<"doughnut">;
  centerText?: string;
  loading: boolean;
  sx?: object;
  legendPosition?: "bottom" | "right";
}

const createCenterTextPlugin = (centerText: string): Plugin<"doughnut"> => ({
  id: "centerText",
  beforeDraw(chart: Chart<"doughnut">) {
    const { ctx } = chart;
    const { top, bottom, left, right, width, height } = chart.chartArea;
    const xCenter = (left + right) / 2;
    const yCenter = (top + bottom) / 2;

    ctx.save();
    const fontSize = Math.min(width, height) / 10; // Adjust font size relative to chart dimensions
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(centerText, xCenter, yCenter);
    ctx.restore();
  },
});

const DoughnutChart: React.FC<DoughnutChartProps> = ({
  data,
  centerText,
  loading,
  sx = {},
  legendPosition = 'right',
}) => {
  const centerTextPlugin = createCenterTextPlugin(`${centerText}`);

  const chartOptions: ChartOptions<"doughnut"> = {
    plugins: {
      legend: {
        position: legendPosition,
        labels: {
          boxWidth: 20,
          generateLabels: function (chart): LegendItem[] {
            const dataset = chart.data.datasets[0];
            const total = dataset.data.reduce((sum, value) => {
              if (typeof value === "number" && typeof sum === "number")
                return sum + value;
              return sum;
            }, 0);

            return (chart.data.labels || []).map((label, index) => {
              const value = dataset.data[index];

              let fillStyle: string | undefined;

              if (Array.isArray(dataset.backgroundColor)) {
                fillStyle = dataset.backgroundColor[index] as string;
              }

              let strokeStyle: string | undefined;
              if (Array.isArray(dataset.borderColor)) {
                strokeStyle = dataset.borderColor[index] as string;
              }

              const labelText = label ? String(label) : "Unknown";

              if (typeof value === "number" && typeof total === "number") {
                const percentage = ((!total ? 0 : (value / total)) * 100).toFixed(0) + "%";
                return {
                  text: label ? `${labelText}: ${percentage}` : "",
                  fillStyle: fillStyle || "#000",
                  strokeStyle: strokeStyle || "#000",
                  lineWidth: dataset.borderWidth as number,
                  hidden: false,
                  index: index,
                };
              }
              return {
                text: label ? labelText! : "",
                fillStyle: fillStyle || "#000",
                strokeStyle: strokeStyle || "#000",
                lineWidth: dataset.borderWidth as number,
                hidden: false,
                index: index,
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset;
            const total = dataset.data.reduce((sum, value) => {
              if (typeof value === "number") return sum + value;
              return sum;
            }, 0);
            const value = dataset.data[tooltipItem.dataIndex];
            if (typeof value === "number" && total > 0) {
              const percentage = ((value / total) * 100).toFixed(2);
              const customLabel = tooltipItem.label === "Down Time" ? "Down Time (includes breaks)" : tooltipItem.label;
              return `${customLabel}: ${percentage}%`;
            }
            return `${tooltipItem.label}: 0%`;
          },
        },
      },
      datalabels: {
        color: "#333",
        formatter: (value, context) => {
          const total = context.dataset.data.reduce((sum, val) => {
            if (typeof val === "number" && typeof sum == "number")
              return sum + val;
            return sum;
          }, 0);
          if (typeof value === "number" && typeof total == "number") {
            const percentage = ((!total ? 0 : (value / total)) * 100).toFixed(0) + "%";
            return percentage;
          }
          return "0%";
        },
      },
    },
    cutout: "70%",
    maintainAspectRatio: false,
    responsive: true,
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "400px",
        aspectRatio: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...sx,
      }}
    >
      {loading ? (
        <CircularProgress size={60} />
      ) : (
        <Doughnut
          key={centerText}
          data={data}
          options={chartOptions}
          plugins={[centerTextPlugin]}
        />
      )}
    </Box>
  );
};

export default DoughnutChart;
